import React, { Suspense, lazy, memo } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { Navigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import { HelmetProvider } from 'react-helmet-async';
import { AuthProvider } from './context/AuthContext';
import { CartProvider } from './context/CartContext';
import { SiteDataProvider } from './context/SiteDataContext';
import { WishlistProvider } from './context/WishlistContext';
import { Header, Footer } from './components/layout';
import LoadingSpinner from './components/common/LoadingSpinner';
import ProtectedRoute from './components/common/ProtectedRoute';
import PermissionGuard from './components/common/PermissionGuard';
import './styles/global.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'react-toastify/dist/ReactToastify.css';

// Lazy load pages for better performance
const Home = lazy(() => import('./pages/Home/Home'));
const ProductList = lazy(() => import('./pages/Products/ProductList'));
const ProductDetail = lazy(() => import('./pages/Products/ProductDetail'));
const CategoryPage = lazy(() => import('./pages/Categories/CategoryPage'));
const About = lazy(() => import('./pages/About/About'));
const Contact = lazy(() => import('./pages/Contact/Contact'));
const Login = lazy(() => import('./pages/Auth/Login'));
const Register = lazy(() => import('./pages/Auth/Register'));
const ForgotPassword = lazy(() => import('./pages/Auth/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/Auth/ResetPassword'));
const Cart = lazy(() => import('./pages/Cart/Cart'));
const Checkout = lazy(() => import('./pages/Checkout/Checkout'));
const Profile = lazy(() => import('./pages/User/Profile'));
const Wishlist = lazy(() => import('./pages/User/Wishlist'));
const PCConfig = lazy(() => import('./pages/PCConfig/PCConfig'));
const OrderTracking = lazy(() => import('./pages/User/OrderTracking'));
const Compare = lazy(() => import('./pages/Products/Compare'));

// Policy Pages
const TermsConditions = lazy(() => import('./pages/Policies/TermsConditions'));
const WarrantyPolicy = lazy(() => import('./pages/Policies/WarrantyPolicy'));
const ReturnPolicy = lazy(() => import('./pages/Policies/ReturnPolicy'));

// Admin Pages
const AdminLayout = lazy(() => import('./pages/Admin/AdminLayout'));
const AdminDashboard = lazy(() => import('./pages/Admin/Dashboard'));
const AdminProducts = lazy(() => import('./pages/Admin/Products/AdminProducts'));
const ProductForm = lazy(() => import('./pages/Admin/Products/ProductForm'));
const AdminCategories = lazy(() => import('./pages/Admin/Categories/AdminCategories'));
const AdminReviews = lazy(() => import('./pages/Admin/Reviews/AdminReviews'));
const AdminBanners = lazy(() => import('./pages/Admin/Banners/AdminBanners'));
const AdminNotices = lazy(() => import('./pages/Admin/Notices/AdminNotices'));
const AdminSettings = lazy(() => import('./pages/Admin/Settings/AdminSettings'));
const AdminMessages = lazy(() => import('./pages/Admin/Messages/AdminMessages'));
const AdminUsers = lazy(() => import('./pages/Admin/Users/AdminUsers'));
const AdminOrders = lazy(() => import('./pages/Admin/Orders/AdminOrders'));
const AdminCoupons = lazy(() => import('./pages/Admin/Coupons/AdminCoupons'));

// Memoized Layout — Header & Footer won't re-mount on every route change
const PublicLayout = memo(({ children }) => (
  <>
    <Header />
    <main className="main-content">{children}</main>
    <Footer />
  </>
));

function App() {
  return (
    <HelmetProvider>
      <SiteDataProvider>
      <AuthProvider>
        <WishlistProvider>
        <CartProvider>
        <Router>
          <Suspense fallback={<LoadingSpinner />}>
            <Routes>
              {/* Public Routes */}
              <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
              <Route path="/products" element={<PublicLayout><ProductList /></PublicLayout>} />
              <Route path="/categories" element={<Navigate to="/products" replace />} />
              <Route path="/products/:slug" element={<PublicLayout><ProductDetail /></PublicLayout>} />
              <Route path="/categories/:slug" element={<PublicLayout><CategoryPage /></PublicLayout>} />
              <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
              <Route path="/contact" element={<PublicLayout><Contact /></PublicLayout>} />
              <Route path="/cart" element={<PublicLayout><Cart /></PublicLayout>} />
              <Route 
                path="/checkout" 
                element={
                  <ProtectedRoute>
                    <PublicLayout><Checkout /></PublicLayout>
                  </ProtectedRoute>
                } 
              />
              <Route path="/terms" element={<PublicLayout><TermsConditions /></PublicLayout>} />
              <Route path="/warranty-policy" element={<PublicLayout><WarrantyPolicy /></PublicLayout>} />
              <Route path="/return-policy" element={<PublicLayout><ReturnPolicy /></PublicLayout>} />
              <Route path="/pc-config" element={<PublicLayout><PCConfig /></PublicLayout>} />
              <Route path="/compare" element={<PublicLayout><Compare /></PublicLayout>} />
              <Route path="/login" element={<PublicLayout><Login /></PublicLayout>} />
              <Route path="/admin/login" element={<PublicLayout><Login /></PublicLayout>} />
              <Route path="/register" element={<PublicLayout><Register /></PublicLayout>} />
              <Route 
                path="/profile" 
                element={
                  <ProtectedRoute>
                    <PublicLayout><Profile /></PublicLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/wishlist" 
                element={
                  <ProtectedRoute>
                    <PublicLayout><Wishlist /></PublicLayout>
                  </ProtectedRoute>
                } 
              />
              <Route 
                path="/orders/track/:orderNumber" 
                element={
                  <ProtectedRoute>
                    <PublicLayout><OrderTracking /></PublicLayout>
                  </ProtectedRoute>
                } 
              />
              
              {/* Auth Routes (no header/footer) */}
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/reset-password/:token" element={<ResetPassword />} />

              {/* Admin Routes - Protected */}
              <Route
                path="/admin"
                element={
                  <ProtectedRoute adminOnly>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<AdminDashboard />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="products" element={<PermissionGuard section="products" redirect><AdminProducts /></PermissionGuard>} />
                <Route path="products/add" element={<PermissionGuard section="products" action="create" redirect><ProductForm /></PermissionGuard>} />
                <Route path="products/edit/:id" element={<PermissionGuard section="products" action="update" redirect><ProductForm /></PermissionGuard>} />
                <Route path="categories" element={<PermissionGuard section="categories" redirect><AdminCategories /></PermissionGuard>} />
                <Route path="reviews" element={<PermissionGuard section="reviews" redirect><AdminReviews /></PermissionGuard>} />
                <Route path="banners" element={<PermissionGuard section="banners" redirect><AdminBanners /></PermissionGuard>} />
                <Route path="notices" element={<PermissionGuard section="banners" redirect><AdminNotices /></PermissionGuard>} />
                <Route path="settings" element={<PermissionGuard section="settings" redirect><AdminSettings /></PermissionGuard>} />
                <Route path="messages" element={<PermissionGuard section="messages" redirect><AdminMessages /></PermissionGuard>} />
                <Route path="users" element={<PermissionGuard section="users" redirect><AdminUsers /></PermissionGuard>} />
                <Route path="orders" element={<PermissionGuard section="orders" redirect><AdminOrders /></PermissionGuard>} />
                <Route path="coupons" element={<PermissionGuard section="orders" redirect><AdminCoupons /></PermissionGuard>} />
              </Route>

              {/* 404 Not Found */}
              <Route
                path="*"
                element={
                  <PublicLayout>
                    <div className="container py-5 text-center">
                      <h1 className="display-1 text-muted">404</h1>
                      <h2 className="mb-4">Page Not Found</h2>
                      <p className="text-muted mb-4">
                        The page you're looking for doesn't exist or has been moved.
                      </p>
                      <a href="/" className="btn btn-primary">Go to Home</a>
                    </div>
                  </PublicLayout>
                }
              />
            </Routes>
          </Suspense>
          <ToastContainer
            position="top-right"
            autoClose={3000}
            hideProgressBar={false}
            newestOnTop
            closeOnClick
            rtl={false}
            pauseOnFocusLoss
            draggable
            pauseOnHover
            theme="light"
          />
        </Router>
        </CartProvider>
        </WishlistProvider>
      </AuthProvider>
      </SiteDataProvider>
    </HelmetProvider>
  );
}

export default App;
